/**
 * Market Map Detail Panel Component
 * Slide panel từ bên phải hiển thị chi tiết marker
 */
import { useState, useEffect } from 'react'
import {
  HiOutlineX,
  HiOutlineExternalLink,
  HiOutlineLocationMarker,
  HiOutlineHome,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from 'react-icons/hi'
import { IoBedOutline, IoWaterOutline } from 'react-icons/io5'
import { BiArea } from 'react-icons/bi'
import clsx from 'clsx'
import marketMapService from '../../services/marketMapService'

function MarketMapDetailPanel({ marker, onClose, isOpen, isFullscreen = false }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isVisible, setIsVisible] = useState(false)

  // Reset image index when marker changes
  useEffect(() => {
    setCurrentImageIndex(0)
  }, [marker?.id])

  // Animation control
  useEffect(() => {
    if (isOpen) {
      // Small delay for animation
      const timer = setTimeout(() => setIsVisible(true), 10)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
    }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen || !marker) return null

  // Get all images (main image + gallery)
  const allImages = []
  if (marker.image_url) allImages.push(marker.image_url)
  if (marker.images && Array.isArray(marker.images)) {
    allImages.push(...marker.images.filter((img) => img !== marker.image_url))
  }

  const handlePrevImage = (e) => {
    e.stopPropagation()
    setCurrentImageIndex((prev) =>
      prev === 0 ? allImages.length - 1 : prev - 1
    )
  }

  const handleNextImage = (e) => {
    e.stopPropagation()
    setCurrentImageIndex((prev) =>
      prev === allImages.length - 1 ? 0 : prev + 1
    )
  }

  return (
    <>
      {/* Backdrop - click to close */}
      <div
        className={clsx(
          'absolute inset-0 bg-black/20 z-40 transition-opacity duration-500',
          isVisible ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
      />

      {/* Slide Panel from Right */}
      <div
        className={clsx(
          'absolute top-0 right-0 h-full w-96 max-w-[90vw] bg-white shadow-2xl z-50',
          'transition-transform duration-500 ease-out',
          isVisible ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors z-20"
        >
          <HiOutlineX className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto h-full">
          {/* Image Carousel */}
          <div className="relative aspect-[4/3] bg-gray-100">
            {allImages.length > 0 ? (
              <>
                <img
                  src={allImages[currentImageIndex]}
                  alt={marker.name}
                  className="w-full h-full object-cover"
                />

                {/* Image Navigation */}
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                    >
                      <HiOutlineChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                    >
                      <HiOutlineChevronRight className="w-5 h-5" />
                    </button>

                    {/* Image Counter */}
                    <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/60 text-white text-xs rounded-full">
                      {currentImageIndex + 1}/{allImages.length}
                    </div>
                  </>
                )}

                {/* Thumbnail Dots */}
                {allImages.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {allImages.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation()
                          setCurrentImageIndex(idx)
                        }}
                        className={clsx(
                          'w-2 h-2 rounded-full transition-colors',
                          idx === currentImageIndex
                            ? 'bg-white'
                            : 'bg-white/50 hover:bg-white/70'
                        )}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <HiOutlineHome className="w-16 h-16 text-gray-300" />
              </div>
            )}

            {/* Badges */}
            <div className="absolute top-3 left-3 flex gap-2">
              {marker.is_featured && (
                <div className="px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded">
                  Noi bat
                </div>
              )}
              {marker.property_status && (
                <div className="px-2 py-1 bg-primary-600 text-white text-xs font-medium rounded">
                  {marker.property_status}
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-5 space-y-4 pb-8">
            {/* Name & Address */}
            <div>
              <h2 className="text-xl font-bold text-gray-900">{marker.name}</h2>
              {marker.address && (
                <p className="text-sm text-gray-500 mt-1 flex items-start gap-1">
                  <HiOutlineLocationMarker className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{marker.address}</span>
                </p>
              )}
            </div>

            {/* Price Box */}
            <div className="bg-primary-50 rounded-xl p-4">
              <div className="text-sm text-primary-600 font-medium">Gia</div>
              <div className="text-2xl font-bold text-primary-700">
                {marker.price_text || marketMapService.formatPrice(marker.price_value)}
              </div>
              {marker.price_value && (
                <div className="text-sm text-gray-500 mt-1">
                  {new Intl.NumberFormat('vi-VN').format(marker.price_value)} VND
                </div>
              )}
            </div>

            {/* Quick Info */}
            {(marker.area_m2 || marker.bedrooms || marker.bathrooms) && (
              <div className="grid grid-cols-3 gap-3">
                {marker.area_m2 && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <BiArea className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                    <div className="text-sm font-medium text-gray-900">
                      {marker.area_text || `${marker.area_m2} m2`}
                    </div>
                    <div className="text-xs text-gray-500">Dien tich</div>
                  </div>
                )}
                {marker.bedrooms && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <IoBedOutline className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                    <div className="text-sm font-medium text-gray-900">
                      {marker.bedrooms}
                    </div>
                    <div className="text-xs text-gray-500">Phong ngu</div>
                  </div>
                )}
                {marker.bathrooms && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <IoWaterOutline className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                    <div className="text-sm font-medium text-gray-900">
                      {marker.bathrooms}
                    </div>
                    <div className="text-xs text-gray-500">Phong tam</div>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {marker.description && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Mo ta</h3>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {marker.description}
                </p>
              </div>
            )}

            {/* External Link */}
            {marker.external_url && (
              <a
                href={marker.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 text-sm text-primary-600 hover:text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <HiOutlineExternalLink className="w-4 h-4" />
                Xem chi tiet
              </a>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default MarketMapDetailPanel
